import React from 'react';
import { DollarSign, TrendingDown, PieChart } from 'lucide-react';
import { costData } from '../data/mockData';
import { Button } from '../components/ui/button';

export const Cost = () => {
  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-slate-500" />
              Cost Intelligence
            </h1>
            <p className="text-sm text-slate-500 mt-1">Map infrastructure to spend, find optimization opportunities</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Monthly Cost', value: `$${costData.totals.total.toLocaleString()}`, icon: DollarSign, trend: null },
            { label: 'Infrastructure', value: `$${costData.totals.infrastructure.toLocaleString()}`, icon: PieChart, trend: '+3%' },
            { label: 'Third-Party', value: `$${costData.totals.thirdParty.toLocaleString()}`, icon: DollarSign, trend: '+1%' },
            { label: 'Potential Savings', value: `$${costData.totals.potentialSavings.toLocaleString()}`, icon: TrendingDown, trend: null }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-lg border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-primary/10">
                  <stat.icon className="w-4.5 h-4.5 text-primary" />
                </div>
                {stat.trend && (
                  <span className="text-xs text-slate-500">{stat.trend}</span>
                )}
              </div>
              <p className="text-2xl font-semibold text-slate-900 mb-1">{stat.value}</p>
              <p className="text-sm text-slate-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Checkout Flow Cost Breakdown */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <h2 className="text-base font-semibold text-slate-900">CHECKOUT FLOW - TOTAL COST OF OWNERSHIP</h2>
            <p className="text-sm text-slate-500">Period: Last 30 days</p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-slate-100">
            {/* Infrastructure */}
            <div className="bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Infrastructure</h3>
                <span className="text-lg font-semibold text-slate-900">${costData.totals.infrastructure.toLocaleString()}/mo</span>
              </div>
              <div className="space-y-2">
                {costData.infrastructure.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-slate-600">{item.item}</span>
                    <span className="font-medium">${item.cost.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Third-Party */}
            <div className="bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Third-Party Services</h3>
                <span className="text-lg font-semibold text-slate-900">${costData.totals.thirdParty.toLocaleString()}/mo</span>
              </div>
              <div className="space-y-2">
                {costData.thirdParty.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-slate-600">{item.item}</span>
                    <span className="font-medium">${item.cost.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* People */}
            <div className="bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">People (allocated)</h3>
                <span className="text-lg font-semibold text-slate-900">${costData.totals.people.toLocaleString()}/mo</span>
              </div>
              <div className="space-y-2">
                {costData.people.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-slate-600">{item.item}</span>
                    <span className="font-medium">${item.cost.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="px-6 py-5 bg-slate-50 border-t border-slate-100">
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-slate-500">Total Cost</p>
                <p className="text-2xl font-semibold text-slate-900">${costData.totals.total.toLocaleString()}/mo</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Cost per transaction</p>
                <p className="text-xl font-semibold text-slate-900">${costData.totals.costPerTransaction}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Revenue per transaction</p>
                <p className="text-xl font-semibold text-slate-900">${costData.totals.revenuePerTransaction}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Margin contribution</p>
                <p className="text-xl font-semibold text-primary">{costData.totals.marginContribution}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Optimization Opportunities */}
        <div>
          <h2 className="text-base font-semibold text-slate-900 mb-4">Optimization Opportunities</h2>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="divide-y divide-slate-100">
              {costData.optimizations.map((opt, idx) => (
                <div key={idx} className="px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <TrendingDown className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{opt.item}</p>
                      <p className="text-sm text-slate-500">Estimated monthly savings</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-semibold text-primary">-${opt.savings.toLocaleString()}/mo</span>
                    <Button variant="outline">
                      Implement
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-5 py-4 bg-primary/10 border-t border-primary/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900">Total Potential Savings</p>
                  <p className="text-sm text-slate-500">12% of total monthly cost</p>
                </div>
                <span className="text-2xl font-semibold text-primary">${costData.totals.potentialSavings.toLocaleString()}/month</span>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};